import { getAnalyticsProvider } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { CapiProviderName } from "@next-platform/packages-ts-types";

import resolveCampaignswellEventName from "@/features/tracking/providers/campaignswell/campaignswellEventMap";
import {
  type CampaignswellWireParams,
  CW_PARAM_KEY_CLIENT_USER_ID,
  CW_PARAM_KEY_CURRENCY,
  CW_PARAM_KEY_CUSTOMER_ID,
  CW_PARAM_KEY_PAYMENT_SYSTEM,
  CW_PARAM_KEY_PRICE,
  CW_PARAM_KEY_PRODUCT_ID,
  CW_PARAM_KEY_SUBSCRIPTION_ID,
  CW_PARAM_KEY_TRANSACTION_ID,
} from "@/features/tracking/providers/campaignswell/campaignswellEvents";
import { markConversionProviderReady, registerConversionProvider } from "@/features/tracking/registry/conversionProviderRegistry";
import { type FireConversionParams } from "@/features/tracking/registry/fireConversion.types";
import AnalyticsProvider from "@/utils/analyticsProvider";

const dispatchCampaignswellLogger = appLogger.withTag("CampaignswellDispatch");

/**
 * Absent and blank are both encoded as "key not sent". GA4 can record an
 * explicit `undefined` as the string `"undefined"`, and a `""` id would
 * break a CampaignsWell join just as badly as a bogus one — this is the
 * last gate before the data leaves for a third party, so it doesn't
 * assume upstream call sites already normalized.
 */
function putIfNonBlank(params: CampaignswellWireParams, key: string, value: string | undefined): void {
  if (value !== undefined && value !== "") {
    params[key] = value;
  }
}

function buildCampaignswellParams(conversionParams: FireConversionParams): CampaignswellWireParams {
  const params: CampaignswellWireParams = {};
  putIfNonBlank(params, CW_PARAM_KEY_SUBSCRIPTION_ID, conversionParams.subscriptionId);
  putIfNonBlank(params, CW_PARAM_KEY_TRANSACTION_ID, conversionParams.transactionId);
  putIfNonBlank(params, CW_PARAM_KEY_CUSTOMER_ID, conversionParams.customerId);
  putIfNonBlank(params, CW_PARAM_KEY_CLIENT_USER_ID, conversionParams.clientUserId);
  putIfNonBlank(params, CW_PARAM_KEY_PAYMENT_SYSTEM, conversionParams.paymentSystem);
  putIfNonBlank(params, CW_PARAM_KEY_CURRENCY, conversionParams.currency);
  putIfNonBlank(params, CW_PARAM_KEY_PRODUCT_ID, conversionParams.productId);
  if (conversionParams.value !== undefined) {
    params[CW_PARAM_KEY_PRICE] = conversionParams.value;
  }
  return params;
}

/**
 * Emit a conversion as a CampaignsWell GA4 custom event.
 *
 * CampaignsWell reads our GA4 property instead of running its own pixel,
 * so this rides the app's analytics provider — there is no vendor script
 * to inject, and no `eventID` dedup option (Meta's mechanism has no GA4
 * equivalent, so `eventId` is unused here by design).
 *
 * Conversions fired before GA4 has an instance go through
 * `AnalyticsProvider.queueEvent`, the same escape hatch `useQueueLogEvent`
 * uses; the static queue is flushed when the instance is constructed.
 */
export default function dispatchCampaignswellConversion(conversionParams: FireConversionParams): void {
  const campaignswellEventName = resolveCampaignswellEventName(conversionParams.eventKey);
  if (campaignswellEventName === undefined) {
    return;
  }

  const params = buildCampaignswellParams(conversionParams);

  try {
    if (!AnalyticsProvider.isInitialized()) {
      AnalyticsProvider.queueEvent(campaignswellEventName, params);
      return;
    }
    getAnalyticsProvider()?.logEvent(campaignswellEventName, params);
  } catch (error: unknown) {
    dispatchCampaignswellLogger.warn("GA4 logEvent threw; dropping event", {
      error,
      eventKey: conversionParams.eventKey,
      campaignswellEventName,
      eventId: conversionParams.eventId,
    });
  }
}

registerConversionProvider({
  id: CapiProviderName.Campaignswell,
  dispatch: dispatchCampaignswellConversion,
});
// Ready at module load: unlike a pixel provider there is no async script
// to wait for, and earliness is already handled one layer down by
// `AnalyticsProvider`'s queue. Leaving the latch unset would make the
// registry hold every conversion forever.
markConversionProviderReady(CapiProviderName.Campaignswell);
